import { useState, useEffect } from 'react';
import { Link2, Plus, X, Check, RefreshCw, AlertTriangle, Megaphone, DownloadCloud } from 'lucide-react';
import { filesAPI } from '../api';
import './ExternalSubsPanel.css';

const GB = 1024 ** 3;

/** Байты в «27.3 ГБ». Общего форматтера в проекте нет, а нужен он только здесь. */
const fmtBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= GB) return `${(n / GB).toFixed(1)} ГБ`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(0)} МБ`;
  return `${(n / 1024).toFixed(0)} КБ`;
};

/**
 * Остаток срока источника. `expire` — секунды epoch; 0 и null означают «бессрочно»
 * (панели присылают именно 0), поэтому отличать их от настоящей даты обязательно.
 */
const expiryOf = (expire) => {
  if (!expire) return { text: 'бессрочно', bad: false };
  const days = Math.ceil((expire * 1000 - Date.now()) / 86400000);
  if (days < 0) return { text: 'истекла', bad: true };
  if (days === 0) return { text: 'истекает сегодня', bad: true };
  return { text: `${days} дн.`, bad: days <= 3 };
};

/**
 * Чужие подписки, привязанные к файлу.
 *
 * Смысл: содержимое файла (ссылки, вписанные руками) — это не всё, что получит клиент.
 * Сюда можно привязать url ЧУЖОЙ подписки, и сервер подмешает её входы к нашим в момент
 * отдачи /subs. Клиент по-прежнему держит ОДИН наш url — про источники он не знает.
 *
 * Список правится локально и уезжает на сервер целиком по «Сохранить»: посылать PUT на
 * каждую букву в поле url бессмысленно, а частичных обновлений у роута нет.
 *
 * Рядом с каждым источником показываем его собственный лимит — сколько трафика съедено
 * из скольки и сколько дней осталось. Это приходит в заголовках ответа чужой панели
 * (`subscription-userinfo`), то же самое рисует V2Box у себя. Цифры относятся к ИСТОЧНИКУ,
 * не к нашему файлу: они нужны, чтобы вовремя заметить, что купленная подписка на исходе.
 *
 * «Обновить» — отдельная от «Проверить» вещь: проверка только смотрит, жив ли источник,
 * а обновление вписывает в файл свежую выдачу источников, заменяя ею только прошлую чужую
 * часть. Ссылки, вписанные руками, не трогаются, поэтому подтверждения больше нет:
 * стереть наши VPS-входы обновлением нельзя.
 */
function ExternalSubsPanel({ fileName, onError, isDirty = false, onContentReplaced }) {
  const [urls, setUrls] = useState([]);
  const [status, setStatus] = useState([]);
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  useEffect(() => { load(); }, [fileName]);

  /**
   * Плашки проверки и обновления — это сообщения о СОБЫТИИ, а не состояние источника.
   * Висевшая бессрочно, такая плашка врёт: «отдал 30 ссылок» осталось на экране рядом
   * со значком HTTP 404 на карточке, и было непонятно, чему верить. Состояние живёт на
   * карточке и обновляется при каждом обращении, поэтому сообщение просто гасим.
   */
  useEffect(() => {
    if (!testResult && !refreshResult) return;
    const t = setTimeout(() => { setTestResult(null); setRefreshResult(null); }, 10000);
    return () => clearTimeout(t);
  }, [testResult, refreshResult]);

  const load = async () => {
    try {
      const res = await filesAPI.getExternalSubs(fileName);
      setUrls(res.data.urls || []);
      setStatus(res.data.status || []);
    } catch (err) {
      if (err.response?.status !== 401) {
        onError?.('Не удалось прочитать внешние подписки: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const statusOf = (url) => status.find((s) => s.url === url);

  const addDraft = () => {
    const url = draft.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      onError?.('Нужен http(s) url подписки');
      return;
    }
    if (urls.includes(url)) {
      onError?.('Этот url уже привязан');
      setDraft('');
      return;
    }
    setUrls([...urls, url]);
    setDraft('');
    setSaved(false);
  };

  const removeUrl = (url) => {
    setUrls(urls.filter((u) => u !== url));
    setSaved(false);
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const res = await filesAPI.setExternalSubs(fileName, urls);
      setUrls(res.data.urls || []);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      // Сервер сбросил кэш этих источников — перечитываем состояние, иначе на карточках
      // остались бы старые счётчики.
      load();
    } catch (err) {
      onError?.('Ошибка сохранения: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  // Проверка идёт мимо кэша — нужна именно чтобы увидеть, жив ли источник ПРЯМО СЕЙЧАС.
  const test = async (url) => {
    setTesting(url);
    setTestResult(null);
    // Гасим итог обновления: иначе рядом висели бы две плашки от разных действий —
    // старый успех проверки и свежая ошибка обновления, и непонятно, что сейчас правда.
    setRefreshResult(null);
    try {
      const res = await filesAPI.testExternalSub(fileName, url);
      setTestResult({ url, ...res.data });
      if (res.data.success) load();
    } catch (err) {
      setTestResult({ url, success: false, error: err.response?.data?.error || err.message });
    } finally {
      setTesting(null);
    }
  };

  /**
   * Обновление ходит за источниками мимо кэша и переписывает в файле только их часть —
   * наши строки остаются нетронутыми. Открытый в редакторе текст после этого устарел,
   * поэтому новое содержимое отдаём наверх: иначе следующее «Сохранить» вернуло бы
   * прошлую выдачу источника обратно.
   */
  const refresh = async () => {
    setIsRefreshing(true);
    setRefreshResult(null);
    setTestResult(null);
    try {
      const res = await filesAPI.refreshExternalSubs(fileName);
      if (res.data.success) {
        setRefreshResult({ ok: true, ...res.data });
        onContentReplaced?.(res.data.content);
      } else {
        setRefreshResult({ ok: false, error: res.data.error });
      }
      setStatus(res.data.status || []);
    } catch (err) {
      onError?.('Не удалось обновить: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsRefreshing(false);
    }
  };

  const listDirty = JSON.stringify(urls) !== JSON.stringify((status || []).map((s) => s.url));

  return (
    <div className="footer-section">
      <div className="section-label">
        <span className="section-label-text"><Link2 size={12} /> Привязанные подписки</span>
        {urls.length > 0 && (
          <span className="extsub-head-actions">
            <span className="extsub-count">{urls.length}</span>
            <button
              className="btn btn-ghost btn-xs extsub-refresh"
              onClick={refresh}
              disabled={isRefreshing || isDirty || listDirty}
              title={
                isDirty ? 'Сначала сохрани изменения в тексте файла'
                  : listDirty ? 'Сначала сохрани список источников'
                  : 'Обновить в файле ссылки источников; вписанные руками не тронутся'
              }
            >
              <DownloadCloud size={12} className={isRefreshing ? 'extsub-spin' : ''} />
              {isRefreshing ? 'Обновляю...' : 'Обновить'}
            </button>
          </span>
        )}
      </div>

      <div className="extsub-add">
        <input
          type="text"
          className="input input-sm"
          placeholder="https://чужая-панель/sub/xxxx — url чужой подписки"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addDraft()}
          spellCheck={false}
        />
        <button className="btn btn-ghost btn-sm btn-icon" onClick={addDraft} title="Добавить в список">
          <Plus size={14} />
        </button>
      </div>

      {urls.length > 0 && (
        <div className="extsub-list">
          {urls.map((url) => {
            const st = statusOf(url);
            const ui = st?.info?.userinfo;
            // total=0 у панелей означает безлимит, а не «ноль осталось» — полоску тогда не рисуем.
            const used = ui ? (ui.upload || 0) + (ui.download || 0) : 0;
            const hasQuota = !!(ui && ui.total);
            const pct = hasQuota ? Math.min(100, (used / ui.total) * 100) : 0;
            const exp = ui ? expiryOf(ui.expire) : null;

            return (
              <div key={url} className="extsub-item">
                <div className="extsub-url" title={url}>
                  {st?.info?.title && <span className="extsub-title">{st.info.title}</span>}
                  {url}
                </div>
                <div className="extsub-meta">
                  {st ? (
                    st.lastError
                      ? <span className="extsub-bad"><AlertTriangle size={10} /> {st.lastError}</span>
                      : <span className="extsub-ok"><Check size={10} /> {st.count} ссылок</span>
                  ) : (
                    <span className="extsub-idle">не запрашивался</span>
                  )}
                  {ui && (
                    <span className="extsub-quota">
                      {hasQuota ? `${fmtBytes(used)} / ${fmtBytes(ui.total)}` : `${fmtBytes(used)} · безлимит`}
                    </span>
                  )}
                  {exp && (
                    <span className={`extsub-expiry${exp.bad ? ' bad' : ''}`}>{exp.text}</span>
                  )}
                </div>
                <button
                  className="btn btn-ghost btn-xs btn-icon"
                  onClick={() => test(url)}
                  disabled={testing === url}
                  title="Только проверить, жив ли источник — в файл не пишет"
                >
                  <RefreshCw size={12} className={testing === url ? 'extsub-spin' : ''} />
                </button>
                <button
                  className="btn btn-ghost btn-xs btn-icon"
                  onClick={() => removeUrl(url)}
                  title="Отвязать"
                >
                  <X size={12} />
                </button>

                {/* Полоска и плашка идут ПОСЛЕ кнопок: карточка — flex-wrap, и элементы
                    на всю ширину переносятся строкой ниже, не ломая ряд с кнопками. */}
                {hasQuota && (
                  <div className="extsub-bar">
                    <div
                      className={`extsub-bar-fill${pct >= 90 ? ' bad' : ''}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
                {st?.info?.announce && (
                  <div className="extsub-announce">
                    <Megaphone size={10} /> {st.info.announce}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {refreshResult && (
        <div className={`extsub-test ${refreshResult.ok ? 'good' : 'bad'}`}>
          {refreshResult.ok
            ? <>В файле {refreshResult.total} ссылок: наших {refreshResult.own} + {refreshResult.external} с
                {' '}{refreshResult.sources} источников
                {refreshResult.removed ? `, прошлых чужих заменено ${refreshResult.removed}` : ''}</>
            : <>{refreshResult.error}</>}
        </div>
      )}

      {testResult && (
        <div className={`extsub-test ${testResult.success ? 'good' : 'bad'}`}>
          {testResult.success
            ? <>Источник отдал {testResult.count} ссылок — это только проверка, в файл они попадут по «Обновить»</>
            : <>Не отвечает: {testResult.error}</>}
        </div>
      )}

      {(listDirty || urls.length > 0) && (
        <div className="extsub-actions">
          <button className="btn btn-primary btn-sm" onClick={save} disabled={isSaving}>
            {saved ? <><Check size={13} /> Сохранено</> : (isSaving ? 'Сохранение...' : 'Сохранить список')}
          </button>
        </div>
      )}

      <p className="enddate-hint">
        Входы с этих url подмешиваются к ссылкам файла при отдаче клиенту — наши идут первыми,
        дубли убираются. Клиенту по-прежнему нужен только наш url с ключом. Ответ источника
        кэшируется ~10 минут; если источник упал, отдаётся его последний удачный список.
        Трафик и срок — это остаток <b>источника</b>, а не нашего файла. «Обновить» вписывает
        в файл свежие ссылки источников <b>на место прошлых</b>: строки, вписанные руками
        (наши VPS-входы), не трогаются — стереть их обновлением нельзя. Если источник
        не ответил, файл не трогается вовсе.
      </p>
    </div>
  );
}

export default ExternalSubsPanel;
